import type { Bindings } from "pino";

import { getLogger, type Logger } from "@expert/logging";
import { createContext, useContext, useMemo, useRef } from "react";

// Default logger used when no LoggerContext is present (e.g., in tests, or
// before Workspace mounts). Partner defaults to "unknown" so errors firing
// pre-mount are tagged explicitly rather than field-absent.
const rootLogger = getLogger({ partner: "unknown" });

/**
 * @example
 * ```tsx
 * // Providing context
 * const logger = useLogger({ sessionId, partner, module: "Workspace" });
 * <LoggerContext value={logger}>
 *     <App />
 * </LoggerContext>
 * ```
 */
export const LoggerContext = createContext<Logger>(rootLogger);

/**
 * Options for useLogger hook.
 * Extends Pino's Bindings to allow any custom fields while providing
 * autocomplete for recommended options (module, supportTeam).
 */
interface UseLoggerOptions extends Bindings {
    /** Session ID - typically from useSession */
    sessionId?: string;
    /** Partner - typically from the current task */
    partner?: string;
    /** Call SID - only for voice tasks */
    callSid?: string;
    /** Chat ID - only for chat tasks */
    chatId?: string;
    /** Module name for this scope */
    module?: string;
    /** Support team owning this code */
    supportTeam?: "sales" | "solve" | "training" | "platform" | "messaging";
}

/**
 * React hook to get the logger from context.
 *
 * The returned logger already has sessionId, partner, callSid, and chatId bound to it
 * (from the nearest LoggerContext), so you don't need to include these in every log call.
 *
 * @example
 * ```tsx
 * // Simple usage - inherit all context from provider
 * const logger = useLogger();
 *
 * // With module name - creates a child logger
 * const logger = useLogger({ module: "MyComponent" });
 *
 * // With module and support team (recommended options with autocomplete)
 * const logger = useLogger({ module: "IntegratedFlow", supportTeam: "sales" });
 *
 * // With all bindings for providing to context
 * const logger = useLogger({ sessionId, partner, callSid, module: "Workspace" });
 * <LoggerContext value={logger}>...</LoggerContext>
 *
 * // Logging - bound fields are automatically included
 * logger.info({ someData }, "Something happened");
 * ```
 */
export function useLogger(options?: UseLoggerOptions): Logger {
    const loggerFromContext = useContext(LoggerContext);
    const loggerRef = useRef<Logger>(rootLogger);

    // Update latest synchronously so it's never stale when the proxy is invoked
    loggerRef.current = options ? loggerFromContext.child(options) : loggerFromContext;

    return useMemo(() => createStableLogger(loggerRef), []);
}

/**
 * Returns a stable Logger that forwards all calls to the logger in loggerRef.
 * Identity is stable across renders; behavior always uses current context.
 */
function createStableLogger(loggerRef: { current: Logger }): Logger {
    return new Proxy({} as Logger, {
        get(_, propertyKey: keyof Logger) {
            const current = loggerRef.current;
            const value = current[propertyKey];
            if (typeof value === "function") return value.bind(current);
            return value;
        },
    });
}
